import { useCallback, useState } from 'react';
import { useApi, useMutation } from './useApi';
import {
  getSubscriptions,
  getSubscription,
  createSubscription,
  updateSubscription,
  SubscriptionFilters,
} from '../services/api';
import {
  Subscription,
  PaginatedResponse,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
} from '../types';

export function useSubscriptions(initialFilters: SubscriptionFilters = {}) {
  const [filters, setFilters] = useState<SubscriptionFilters>(initialFilters);

  const fetchSubscriptions = useCallback(() => getSubscriptions(filters), [filters]);

  const { data, loading, error, refetch } = useApi<PaginatedResponse<Subscription>>(
    fetchSubscriptions,
    [JSON.stringify(filters)]
  );

  return {
    subscriptions: data?.items || [],
    total: data?.total || 0,
    page: data?.page || 1,
    totalPages: data?.totalPages || 1,
    loading,
    error,
    filters,
    setFilters,
    refetch,
  };
}

export function useSubscription(id: string | undefined) {
  const fetchSubscription = useCallback(() => {
    if (!id) return Promise.reject(new Error('No subscription ID provided'));
    return getSubscription(id);
  }, [id]);

  const { data, loading, error, refetch } = useApi<Subscription>(fetchSubscription, [id]);

  return {
    subscription: data,
    loading,
    error,
    refetch,
  };
}

export function useCreateSubscription() {
  const { mutate, loading, error, data, reset } = useMutation<Subscription, CreateSubscriptionRequest>(
    createSubscription
  );

  return {
    createSubscription: mutate,
    loading,
    error,
    createdSubscription: data,
    reset,
  };
}

export function useUpdateSubscription() {
  const { mutate, loading, error, data, reset } = useMutation<
    Subscription,
    { id: string; data: UpdateSubscriptionRequest }
  >(({ id, data }) => updateSubscription(id, data));

  return {
    updateSubscription: mutate,
    loading,
    error,
    updatedSubscription: data,
    reset,
  };
}
